import { LanguageNameSpaces, OpenMessage } from "@shared/types";
import { useEffect, useRef, useState } from "react";
import { IS_NUMERIC_REGEX } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useVerificationCodeProps {
  numOfDigits: number;
  onSubmit: (code: string) => void;
  defaultCode?: string;
  onChange?: (code: string) => void;
  isKeyboardOpen?: boolean;
  openMessage: OpenMessage;
  autoFocus?: boolean;
}

const useVerificationCode = ({
  numOfDigits,
  onSubmit,
  defaultCode,
  onChange: propsOnChange,
  isKeyboardOpen,
  openMessage,
  autoFocus,
}: useVerificationCodeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [code, setCode] = useState("");
  const inputRef = useRef<any>(null);
  const [focused, setFocused] = useState(false);
  const [lastAutoPasteCode, setLastAutoPasteCode] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (!isKeyboardOpen && focused) setFocused(false);
  }, [isKeyboardOpen]);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => inputRef?.current?.focus(), 500);
    }
  }, [autoFocus]);

  const onCodeChange = (code: string) => {
    propsOnChange?.(code);
    setCode(code);
  };

  const validateCode = (code: string) =>
    IS_NUMERIC_REGEX.test(code) && code.length === numOfDigits;
  const isCodeValid = validateCode(code);

  useEffect(() => {
    if (defaultCode && validateCode(defaultCode)) {
      onCodeChange(defaultCode);
    }
  }, [defaultCode]);

  useEffect(() => {
    if (isCodeValid) {
      onSubmit(code);
    }
  }, [code]);

  const replaceValueAtIndex = (index: number, value: string = "") => {
    return code.substring(0, index) + value + code.substring(index + 1);
  };

  const moveBack = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const onBackPress = () => {
    onCodeChange(replaceValueAtIndex(selectedIndex));
    moveBack();
  };

  const onChange = (value: string) => {
    if (!value) return;
    let updatedCode;
    if (selectedIndex === code.length && selectedIndex < numOfDigits - 1) {
      updatedCode = code + value;
    } else {
      updatedCode = replaceValueAtIndex(selectedIndex, value);
    }
    onCodeChange(updatedCode.slice(0, numOfDigits));
    const nextIndex = Math.min(
      Math.max(selectedIndex + value.length, code.length || 0),
      numOfDigits - 1
    );
    setSelectedIndex(nextIndex);
  };

  const pasteCode = (code?: string) => {
    if (code && validateCode(code)) {
      setCode(code);
      setSelectedIndex(numOfDigits - 1);
      return true;
    }
    return false;
  };

  const processClipboardCode = async (
    clipboardText?: string,
    isAutoPaste?: boolean
  ) => {
    if (isAutoPaste) {
      if (lastAutoPasteCode !== clipboardText && pasteCode(clipboardText)) {
        setLastAutoPasteCode(clipboardText);
      }
    } else if (!pasteCode(clipboardText)) {
      openMessage(t("verification_code.invalid_clipboard"), "warning");
    }
  };

  const hiddenInputProps = {
    value: "",
    ref: inputRef,
    onChange,
    onBlur: () => setFocused(false),
    onFocus: () => setFocused(true),
  };

  const buttonProps = {
    type: "primary",
    onClick: () => onSubmit(code || ""),
    disabled: !isCodeValid,
  } as const;

  const onBoxClick = (index: number) => {
    setSelectedIndex(Math.min(index, code.length));
    let timeout = 0;
    if (isKeyboardOpen === false) {
      inputRef.current?.blur();
      timeout = 100;
    }
    setTimeout(() => inputRef.current?.focus(), timeout);
  };

  const boxesData = Array(numOfDigits)
    .fill(0)
    .map((_, index) => {
      const style = index === numOfDigits - 1 ? {} : { marginRight: 8 };
      return {
        style,
        onClick: () => onBoxClick(index),
        isSelected: focused && index === selectedIndex,
        text: code[index],
      };
    });

  return {
    onBackPress,
    hiddenInputProps,
    buttonProps,
    boxesData,
    processClipboardCode,
  };
};

export default useVerificationCode;
